import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useState } from 'react';
import { getMenuList, getMenuModalList } from 'src/apis/menuApi';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import useMenuStore from 'src/store/useMenuStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { buildCommonCodeRequirements, SYS2010_R01_ROW_VALIDATE } from 'src/utils/commonCode/commonCodeRequirementConfig';
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import { fetchFilterConfig, InsertRowHandler } from 'src/utils/grid/GridHandlerContent';
import { validateString } from 'src/utils/string/stringUtil';
import { showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { toNumberSafe } from 'src/utils/type/typeUtil';

import {
	deleteSYS2010_R01,
	exportSYS2010_R01Excel,
	getSYS2010_R01AllCount,
	getSYS2010_R01List,
	patchSYS2010_R01,
	postSYS2010_R01,
} from './Api_SYS2010_R01';
import { defaultSYS2010_R01Dto, SYS2010_R01Dto } from './Dto_SYS2010_R01';
import useSYS2010_R01Store, { defaultSYS2010_R01Filter, SYS2010_R01Filter } from './Store_SYS2010_R01';

interface Props {
	gridRef: React.RefObject<AgGridReact<SYS2010_R01Dto> | null>;
	setMessage: (message: string) => void;
}

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

export const Content_SYS2010_R01 = ({ gridRef, setMessage }: Props) => {
	const {
		SYS2010_R01Filter, SYS2010_R01PastFilter, setSYS2010_R01PastFilter,
		SYS2010_R01List, setSYS2010_R01List, insertSYS2010_R01List,
		deletedSYS2010_R01List, addDeletedSYS2010_R01List, removeDeletedSYS2010_R01List,
		selectedSYS2010_R01List, setSelectedSYS2010_R01List, removeSelectedSYS2010_R01List,
		setOriginalSYS2010_R01List,
		setAllCount,
		setScrollTop,
		initData,
	} = useSYS2010_R01Store();

	const {
		setMenuCdList,
		fetchMenuCdCode,
		devloperList,
		devStsList,
		yesNoList,
	} = useCommonCodeStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const { setMenuList } = useMenuStore();
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	useFetchActionbarAuth('SYS2010_R01');

	const [clickable, setClickable] = useState(true);

	const getParams = (filter: SYS2010_R01Filter): Partial<SYS2010_R01Filter> => ({
		menuCd: filter.menuCd || undefined,
		menuNm: filter.menuNm || undefined,
		useYn: filter.useYn || undefined,
		devSts: filter.devSts || undefined,
	});

	const defaultSYS2010_R01Row = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(SYS2010_R01_ROW_VALIDATE, {
				devloperList,
				devStsList,
				yesNoList,
			}),
		);
		if (!commonResult.valid) { return { ...defaultSYS2010_R01Dto }; }

		return {
			...defaultSYS2010_R01Dto,
			useYn: yesNoList.find((value) => value.minorCd === 'Y')?.minorCd || 'Y',
			displayOnly: yesNoList.find((value) => value.minorCd === 'N')?.minorCd || 'N',
			queryAut: yesNoList.find((value) => value.minorCd === 'Y')?.minorCd || 'Y',
			devloper: devloperList[0].minorCd,
			insertAut: yesNoList.find((value) => value.minorCd === 'Y')?.minorCd || 'Y',
			deleteAut: yesNoList.find((value) => value.minorCd === 'Y')?.minorCd || 'Y',
			saveAut: yesNoList.find((value) => value.minorCd === 'Y')?.minorCd || 'Y',
			printAut: yesNoList.find((value) => value.minorCd === 'Y')?.minorCd || 'Y',
			excelAut: yesNoList.find((value) => value.minorCd === 'Y')?.minorCd || 'Y',
			devSts: Number(devStsList[0].minorCd),
			inserted: true,
		};
	}, [devStsList, devloperList, validateCommonCodeLists, yesNoList]);

	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);
		const { isDefaultFilter, listLength } = fetchFilterConfig(
			SYS2010_R01Filter,
			SYS2010_R01PastFilter,
			defaultSYS2010_R01Filter,
			SYS2010_R01List.length,
		);

		const response = await exportSYS2010_R01Excel(
			isDefaultFilter ? '' : 'search',
			getParams(SYS2010_R01Filter),
			listLength,
		);

		downloadSettingExcel({ responseData: response.data.data, fileName: '메뉴등록-excel.xlsx' });
		setLoadingSpinner(false);
	}, [SYS2010_R01Filter, SYS2010_R01List.length, SYS2010_R01PastFilter, setLoadingSpinner]);

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getSYS2010_R01AllCount(getParams(SYS2010_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [SYS2010_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions,
	) => {
		const messageMode = options?.messageMode ?? 'show';

		setLoadingSpinner(true);
		if (fetchType === 'search') {
			setScrollTop(null);
		}

		const initialListLength = SYS2010_R01List.length;
		if (fetchType === 'refetch') {
			setSYS2010_R01List([]);
		}

		const responseData = await getSYS2010_R01List(
			fetchType === 'refetch' ? 'refetch' : '',
			getParams(SYS2010_R01Filter),
			fetchType === 'refetch' ? initialListLength : SYS2010_R01List.length ?? 0,
		);

		const newList = [...responseData.data.data] as SYS2010_R01Dto[];
		const processAllCount = await renewalAllCount();

		setSYS2010_R01List(newList);
		setOriginalSYS2010_R01List(newList);
		setSelectedSYS2010_R01List([]);
		removeDeletedSYS2010_R01List(deletedSYS2010_R01List);
		gridRef.current?.api.ensureIndexVisible(0, 'top');

		if (newList.length === 0) {
			if (messageMode === 'show') {
				showWarning('데이터가 없습니다.');
			}
		} else if (messageMode === 'show') {
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			setTimeout(() => setMessage(''), 5000);
		}

		setSYS2010_R01PastFilter(SYS2010_R01Filter);
		setLoadingSpinner(false);
	}, [SYS2010_R01Filter, SYS2010_R01List, deletedSYS2010_R01List, gridRef, setMessage, setLoadingSpinner, setOriginalSYS2010_R01List, setSelectedSYS2010_R01List, removeDeletedSYS2010_R01List, renewalAllCount, setScrollTop, setSYS2010_R01List, setSYS2010_R01PastFilter]);

	const fetchData = useCallback(async () => {
		await executeDataFetch('search');
	}, [executeDataFetch]);

	const initContainerData = useCallback(async () => {
		initData();
	}, [initData]);

	const handleInsertClick = useCallback((e: React.MouseEvent, rowIndex?: number) => {
		const isGridInsert = rowIndex !== undefined;

		InsertRowHandler({
			event: e,
			dataList: SYS2010_R01List,
			rowData: isGridInsert
				? SYS2010_R01List[rowIndex]
				: { ...defaultSYS2010_R01Row() as SYS2010_R01Dto, valueIndex: -1 },
			insertRow: insertSYS2010_R01List,
			defaultRowData: defaultSYS2010_R01Row() as SYS2010_R01Dto,
			...(isGridInsert && { setClickable }),
		});
	}, [SYS2010_R01List, insertSYS2010_R01List, defaultSYS2010_R01Row, setClickable]);

	const validateInsertData = useCallback((data: SYS2010_R01Dto, currentRowIndex: number) => {
		if (!validateString(data.menuCd)) {
			showWarning('메뉴코드를 입력해주세요.');
			return false;
		}
		if (!validateString(data.menuNm)) {
			showWarning('메뉴명을 입력해주세요.');
			return false;
		}

		const activeUsers = SYS2010_R01List.filter(
			(menu) => !deletedSYS2010_R01List.includes(menu.menuCd),
		);
		const otherUsers = activeUsers.filter((_, index) => index !== currentRowIndex);

		if (otherUsers.some((menu) => menu.menuCd === data.menuCd)) {
			showWarning(`중복된 메뉴코드가 있습니다.\n메뉴코드: ${data.menuCd}`);
			return false;
		}
		return true;
	}, [SYS2010_R01List, deletedSYS2010_R01List]);

	const handleSaveClick = useCallback(async () => {
		const insertedList: SYS2010_R01Dto[] = SYS2010_R01List.filter((row) =>
			!deletedSYS2010_R01List.includes(row.menuCd) &&
			row.inserted,
		);
		const modifiedList: SYS2010_R01Dto[] = SYS2010_R01List.filter((row) =>
			!deletedSYS2010_R01List.includes(row.menuCd) &&
			row.isModified && !row.inserted,
		);

		let seenMenuSet = new Set<string>();
		let valid = true;
		insertedList.forEach((value) => {
			const index = SYS2010_R01List.findIndex((menu) => menu.menuCd === value.menuCd);
			if (!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.menuCd);
		});
		if (!valid) { return; }

		seenMenuSet = new Set<string>();
		valid = true;
		modifiedList.forEach((value) => {
			const index = SYS2010_R01List.findIndex((menu) => menu.menuCd === value.menuCd);
			if (!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.menuCd);
		});
		if (!valid) { return; }

		const apiInsertedList = insertedList.map((row) => ({
			...row,
			devSts: toNumberSafe(row.devSts),
		})) as SYS2010_R01Dto[];
		const apiModifiedList: SYS2010_R01Dto[] = modifiedList.map((row) => ({
			...row,
			devSts: toNumberSafe(row.devSts),
		})) as SYS2010_R01Dto[];

		const deleteStringList = new Set(deletedSYS2010_R01List.map((item) => item.toString()));
		const deleteTargetList = SYS2010_R01List.filter((item) =>
			deleteStringList.has(item.valueIndex.toString()),
		);

		const [insertedResponse, modifiedResponse, deleteResponse] = await Promise.all([
			apiInsertedList.length > 0 ? postSYS2010_R01(apiInsertedList) : Promise.resolve(),
			apiModifiedList.length > 0 ? patchSYS2010_R01(apiModifiedList) : Promise.resolve(),
			deletedSYS2010_R01List.length > 0
				? Promise.all(deleteTargetList.map((value) => deleteSYS2010_R01(value.menuCd)))
				: Promise.resolve(),
		]);

		if (insertedResponse?.data.successFlag || modifiedResponse?.data.successFlag) {
			showSuccess('메뉴정보 저장이 완료되었습니다.');
			setMessage('메뉴정보 저장이 완료되었습니다.');
		} else if (deleteResponse) {
			showSuccess('메뉴정보 삭제가 완료되었습니다.');
			setMessage('메뉴정보 삭제가 완료되었습니다.');
		}
		setTimeout(() => setMessage(''), 5000);

		setMenuList((await getMenuList()).data.data);
		setMenuCdList((await getMenuModalList()).data.data);
		fetchMenuCdCode();
		await executeDataFetch('refetch', { messageMode: 'none' });
	}, [SYS2010_R01List, deletedSYS2010_R01List, executeDataFetch, fetchMenuCdCode, setMenuCdList, setMenuList, setMessage, validateInsertData]);

	const handleDeleteClick = useCallback(() => {
		if (deletedSYS2010_R01List.length > 0) {
			removeDeletedSYS2010_R01List(deletedSYS2010_R01List);
			removeSelectedSYS2010_R01List();
			setSYS2010_R01List([...SYS2010_R01List]);
		} else if (selectedSYS2010_R01List.length > 0) {
			addDeletedSYS2010_R01List(selectedSYS2010_R01List);
			removeSelectedSYS2010_R01List(selectedSYS2010_R01List);
		}
	}, [setSYS2010_R01List, SYS2010_R01List, deletedSYS2010_R01List, addDeletedSYS2010_R01List, removeDeletedSYS2010_R01List, selectedSYS2010_R01List, removeSelectedSYS2010_R01List]);

	return {
		clickable,
		setClickable,
		handleExcelClick,
		defaultSYS2010_R01Row,
		fetchData,
		initContainerData,
		handleInsertClick,
		handleSaveClick,
		handleDeleteClick,
	};
};
